import { FC, useState } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import { match } from 'ts-pattern'

import {
  Box,
  EmailBodyPreview,
  EmailPreview,
  Skeleton
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

import { getEnvironment } from 'lib/environment/environment'
import { PdfPreviewDrawer } from 'Invoices/index'
import { useNodeTimelineVariant } from 'Automations/components'
import { useTemplateResolver } from '../../hooks'
import {
  getNotificationSenderEmail,
  getReminderEmail,
  useMerchantLogoUrl
} from '../../helpers'
import type { ReminderEmailData } from '../../Reminder.executionSummary.hooks'
import type { Config } from '../..'

// The email preview's content depends on resolved template values, which can
// lag the rest of the timeline; fade it in over the loading skeleton when ready.
const FADE_TRANSITION = { duration: 0.15 } as const

export type ReminderEmailPreviewInvoice = {
  id: string
  invoiceNumber: string
}

export const ReminderEmailPreview: FC<{
  config: Pick<Config, 'to' | 'cc' | 'bcc' | 'subject' | 'template'>
  emailData: ReminderEmailData
  invoice: ReminderEmailPreviewInvoice | undefined
  startCollapsed?: boolean
}> = ({ config, emailData, invoice, startCollapsed }) => {
  const variant = useNodeTimelineVariant()
  const merchantQuery = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )
  const { resolveTemplate, isPending: templateValuesPending } =
    useTemplateResolver({
      invoice: emailData.invoice,
      customer: emailData.customer
    })
  const logoUrl = useMerchantLogoUrl()
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const merchant = merchantQuery.data
  if (!merchant) {
    return null
  }

  const email = getReminderEmail({ config, emailData })

  const attachmentFileName = invoice && `invoice-${invoice.invoiceNumber}.pdf`

  const attachments =
    invoice && attachmentFileName
      ? [
          {
            fileName: attachmentFileName,
            onClick: () => setIsModalOpen(true)
          }
        ]
      : undefined

  return (
    <Box
      marginBottom={match(variant)
        .with('DEFAULT', () => '0px')
        .with('INSPECTOR', () => '12px')
        .exhaustive()}
    >
      <AnimatePresence mode="wait" initial={false}>
        {templateValuesPending ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={FADE_TRANSITION}
          >
            <Skeleton
              width="100%"
              height={match(variant)
                .with('DEFAULT', () => '344px')
                .with('INSPECTOR', () => '112px')
                .exhaustive()}
            />
          </motion.div>
        ) : (
          <motion.div
            key="preview"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={FADE_TRANSITION}
          >
            {match(variant)
              .with('DEFAULT', () => (
                <>
                  <EmailPreview
                    from={{
                      name: merchant.legalCompanyName,
                      email: getNotificationSenderEmail(getEnvironment()),
                      logoUrl
                    }}
                    to={email.to.map(resolveTemplate)}
                    cc={email.cc.map(resolveTemplate)}
                    bcc={email.bcc.map(resolveTemplate)}
                    subject={resolveTemplate(email.subject)}
                    body={resolveTemplate(email.body)}
                    attachments={attachments}
                    startCollapsed={startCollapsed}
                    data-testid="automations.executionSummary.reminder.emailPreview"
                    width="100%"
                  />
                  {invoice && isModalOpen && (
                    // The reminder workflow only runs against an invoice that has
                    // been sent, so the preview can rely on the server-side PDF
                    <PdfPreviewDrawer
                      closeModal={() => setIsModalOpen(false)}
                      invoiceId={invoice.id}
                      invoiceStatus="SENT"
                      invoiceNumber={invoice.invoiceNumber}
                    />
                  )}
                </>
              ))
              .with('INSPECTOR', () => (
                <EmailBodyPreview
                  body={resolveTemplate(email.body)}
                  data-testid="automations.executionSummary.reminder.emailBodyPreview"
                />
              ))
              .exhaustive()}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}
